import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import {
  MODULE_PERMISSIONS,
  ACTION_LABELS,
  GROUP_META,
} from "../config/pagePermissions";
import "./RolePermissionPage.css";

const ModuleCard = ({
  module,
  permissions,
  isAdminSelected,
  toggleModule,
  togglePermission,
}) => {
  const checkedCount = module.actions.filter(
    (action) => permissions?.[module.key]?.[action],
  ).length;

  const moduleChecked = checkedCount === module.actions.length;
  const moduleIndeterminate =
    checkedCount > 0 && checkedCount < module.actions.length;

  return (
    <div className="rp-module-card">
      <div className="rp-module-header">
        <label className="rp-module-title">
          <input
            type="checkbox"
            disabled={isAdminSelected}
            checked={moduleChecked}
            ref={(input) => {
              if (input) input.indeterminate = moduleIndeterminate;
            }}
            onChange={() => toggleModule(module)}
          />
          <span>{module.label}</span>
        </label>

        <span className="rp-progress">
          {checkedCount}/{module.actions.length}
        </span>
      </div>

      <div className="rp-action-grid">
        {module.actions.map((action) => {
          const checked = !!permissions?.[module.key]?.[action];

          return (
            <label
              key={action}
              className={`rp-action-item ${checked ? "checked" : ""} ${
                isAdminSelected ? "disabled" : ""
              }`}
            >
              <input
                type="checkbox"
                disabled={isAdminSelected}
                checked={checked}
                onChange={() => togglePermission(module.key, action)}
              />
              <span>{ACTION_LABELS[action] || action}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const RolePermissionPage = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [newRoleName, setNewRoleName] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const authHeader = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    }),
    [],
  );

  const isAdminSelected =
    String(selectedRole?.roleName || "").toLowerCase() === "admin";

  const groupedModules = useMemo(() => {
    return MODULE_PERMISSIONS.reduce((groups, module) => {
      const groupName = module.group || "Other Permissions";

      if (!groups[groupName]) {
        groups[groupName] = [];
      }

      groups[groupName].push(module);
      return groups;
    }, {});
  }, []);

  const orderedPermissionGroups = useMemo(() => {
    const configuredGroupNames = Object.keys(GROUP_META || {});

    const configuredGroups = configuredGroupNames
      .filter((groupName) => groupedModules[groupName]?.length)
      .map((groupName) => ({
        groupName,
        modules: groupedModules[groupName],
      }));

    const remainingGroups = Object.entries(groupedModules)
      .filter(([groupName]) => !configuredGroupNames.includes(groupName))
      .map(([groupName, modules]) => ({
        groupName,
        modules,
      }));

    return [...configuredGroups, ...remainingGroups];
  }, [groupedModules]);

  const buildFullPermissions = () => {
    const full = {};

    MODULE_PERMISSIONS.forEach((module) => {
      full[module.key] = {};

      module.actions.forEach((action) => {
        full[module.key][action] = true;
      });
    });

    return full;
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = async (keepRoleName = null) => {
    try {
      const currentScrollY = window.scrollY;

      const response = await api.get("/roles", authHeader);
      const roleList = response.data || [];

      setRoles(roleList);

      if (roleList.length) {
        const roleToSelect =
          roleList.find(
            (role) =>
              String(role.roleName || "").toLowerCase() ===
              String(
                keepRoleName || selectedRole?.roleName || "admin",
              ).toLowerCase(),
          ) || roleList[0];

        setSelectedRole(roleToSelect);

        const selectedRoleName = String(
          roleToSelect.roleName || "",
        ).toLowerCase();

        if (selectedRoleName === "admin") {
          setPermissions(buildFullPermissions());
        } else {
          setPermissions(roleToSelect.permissions || {});
        }
      } else {
        setSelectedRole(null);
        setPermissions({});
      }

      requestAnimationFrame(() => {
        window.scrollTo({
          top: currentScrollY,
          behavior: "auto",
        });
      });
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      alert(error.response?.data?.message || "Failed to fetch roles");
    }
  };

  const selectRole = (role) => {
    const currentScrollY = window.scrollY;

    setSelectedRole(role);

    const selectedRoleName = String(role?.roleName || "").toLowerCase();

    if (selectedRoleName === "admin") {
      setPermissions(buildFullPermissions());
    } else {
      setPermissions(role?.permissions || {});
    }

    requestAnimationFrame(() => {
      window.scrollTo({
        top: currentScrollY,
        behavior: "auto",
      });
    });
  };

  const togglePermission = (moduleKey, actionKey) => {
    if (isAdminSelected) return;

    setPermissions((previousPermissions) => ({
      ...previousPermissions,
      [moduleKey]: {
        ...(previousPermissions[moduleKey] || {}),
        [actionKey]: !previousPermissions?.[moduleKey]?.[actionKey],
      },
    }));
  };

  const toggleModule = (module) => {
    if (isAdminSelected) return;

    const allChecked = module.actions.every(
      (action) => permissions?.[module.key]?.[action],
    );

    setPermissions((previousPermissions) => ({
      ...previousPermissions,
      [module.key]: module.actions.reduce((modulePermissions, action) => {
        modulePermissions[action] = !allChecked;
        return modulePermissions;
      }, {}),
    }));
  };

  const savePermissions = async () => {
    if (!selectedRole?.roleName) {
      alert("Select role first");
      return;
    }

    if (isAdminSelected) {
      alert(
        "Admin has default full access. You cannot change Admin permissions.",
      );
      return;
    }

    try {
      setSaving(true);

      const currentRoleName = selectedRole.roleName;
      const currentScrollY = window.scrollY;

      await api.put(
        `/roles/${encodeURIComponent(currentRoleName)}/permissions`,
        { permissions },
        authHeader,
      );

      alert("Permissions saved successfully");
      await fetchRoles(currentRoleName);

      requestAnimationFrame(() => {
        window.scrollTo({
          top: currentScrollY,
          behavior: "auto",
        });
      });
    } catch (error) {
      console.error("Failed to save permissions:", error);
      alert(error.response?.data?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const createRole = async () => {
    const roleName = newRoleName.trim();

    if (!roleName) {
      alert("Enter role name");
      return;
    }

    try {
      const response = await api.post(
        "/roles",
        {
          roleName,
          type: "Saved",
          permissions: {},
        },
        authHeader,
      );

      setNewRoleName("");
      setSelectedRole(response.data);
      setPermissions({});

      await fetchRoles(roleName);
    } catch (error) {
      console.error("Failed to create role:", error);
      alert(error.response?.data?.message || "Failed to create role");
    }
  };

  const filteredRoles = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return roles;
    }

    return roles.filter((role) =>
      String(role.roleName || "")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [roles, search]);

  const getGroupClassName = (groupName) =>
    String(groupName || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  return (
    <div className="rp-page">
      <aside className="rp-roles-panel">
        <h2 className="rp-title">Roles</h2>
        <p className="rp-subtitle">Select role and assign permissions</p>

        <div className="rp-create-box">
          <input
            value={newRoleName}
            onChange={(event) => setNewRoleName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                createRole();
              }
            }}
            placeholder="Create new role"
            className="rp-input"
          />

          <button type="button" onClick={createRole} className="rp-add-btn">
            + Add
          </button>
        </div>

        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search role..."
          className="rp-search"
        />

        <div className="rp-role-list">
          {filteredRoles.map((role, index) => {
            const active = selectedRole?.roleName === role.roleName;
            const isAdmin =
              String(role.roleName || "").toLowerCase() === "admin";

            return (
              <div
                key={`${role.roleName}-${index}`}
                onClick={() => selectRole(role)}
                onMouseDown={(event) => event.preventDefault()}
                className={`rp-role-card ${active ? "active" : ""}`}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    selectRole(role);
                  }
                }}
              >
                <div className="rp-role-number">{index + 1}</div>

                <div className="rp-role-content">
                  <div className="rp-role-name">
                    {role.roleName}
                    {isAdmin && <span className="rp-locked">LOCKED</span>}
                  </div>

                  <div className="rp-role-type">{role.type || "Saved"}</div>
                </div>

                <div className="rp-arrow">›</div>
              </div>
            );
          })}

          {filteredRoles.length === 0 && (
            <div className="rp-empty">No roles found</div>
          )}
        </div>
      </aside>

      <main className="rp-details-panel">
        <div className="rp-topbar">
          <div>
            <h2 className="rp-title">Role Details</h2>
            <p className="rp-subtitle">
              {selectedRole
                ? `Manage access for ${selectedRole.roleName}`
                : "Select role"}
            </p>
          </div>

          <button
            type="button"
            onClick={savePermissions}
            disabled={isAdminSelected || saving}
            className="rp-save-btn"
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </div>

        {selectedRole ? (
          <>
            <div className="rp-banner">
              <div>
                <div className="rp-banner-title">{selectedRole.roleName}</div>
                <div className="rp-banner-text">
                  {isAdminSelected
                    ? "Admin has full default access. It cannot be changed."
                    : "Select page-wise and function-wise access below."}
                </div>
              </div>

              <span className={isAdminSelected ? "rp-pill locked" : "rp-pill"}>
                {isAdminSelected ? "Locked Full Access" : "Editable"}
              </span>
            </div>

            <div className="rp-grouped-permissions">
              {orderedPermissionGroups.map(({ groupName, modules }) => {
                const groupMeta = GROUP_META?.[groupName] || {
                  icon: "🔐",
                  label: groupName,
                };

                const groupClassName = getGroupClassName(groupName);

                return (
                  <section
                    key={groupName}
                    className={`rp-permission-group rp-group-${groupClassName}`}
                  >
                    <div className="rp-section-heading">
                      <span className="rp-section-icon">{groupMeta.icon}</span>
                      <span className="rp-section-title">
                        {groupMeta.label}
                      </span>
                      <span className="rp-section-count">
                        {modules.length}{" "}
                        {modules.length === 1 ? "Page" : "Pages"}
                      </span>
                    </div>

                    <div className="rp-module-grid">
                      {modules.map((module) => (
                        <ModuleCard
                          key={module.key}
                          module={module}
                          permissions={permissions}
                          isAdminSelected={isAdminSelected}
                          toggleModule={toggleModule}
                          togglePermission={togglePermission}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </>
        ) : (
          <div className="rp-empty">Select role</div>
        )}
      </main>
    </div>
  );
};

export default RolePermissionPage;
